import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { colors } from 'global/variables';
import { headerHeight } from '../TopBar/TopBar';
import {
  type StyledSparkleProps,
  type StyledCardButtonProps,
  type StyledResponseVariantProps,
  RESPONSE_VARIANTS,
} from './RoutingAIChatTypes';

const { aqua, black, primaryBorderColor, secondaryColor, white } = colors;

export const StyledSparkle = styled('span', {
  shouldForwardProp: (prop) => prop !== 'sparkleColor',
})<StyledSparkleProps>(({ sparkleColor = aqua }) => ({
  color: sparkleColor,
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  lineHeight: 1,
}));

export const StyledHeaderIcon = styled.span({
  width: '1.75rem',
  height: '1.75rem',
  borderRadius: '0.375rem',
  backgroundImage: `linear-gradient(135deg, ${aqua}, ${secondaryColor})`,
  color: white,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 1,
});

export const StyledPanel = styled.section({
  position: 'fixed',
  right: '1rem',
  top: `calc(${headerHeight} + 1rem)`,
  bottom: '1rem',
  width: '31.25rem',
  maxWidth: '45vw',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: white,
  border: `1px solid ${primaryBorderColor}`,
  boxShadow: '0 4px 20px rgba(15, 23, 42, 0.1)',
  zIndex: 10101,
  overflow: 'hidden',
  borderRadius: '0.75rem',
  '@media screen and (max-width: 850px)': {
    top: '3rem',
    right: '0.75rem',
    bottom: '0.75rem',
    width: 'calc(100% - 1.5rem)',
    maxWidth: 'none',
    height: 'calc(100% - 3.75rem)',
    borderRadius: '0.75rem',
  },
});

export const StyledDropOverlay = styled.div({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  border: '2px dashed #5cbdb9',
  borderRadius: '0.25rem',
  pointerEvents: 'none',
  fontFamily: 'FiraGO',
  fontSize: '0.9rem',
  color: '#0f1621',
});

export const StyledHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '0.75rem 1rem',
  backgroundColor: white,
  color: '#1d293d',
  flexShrink: 0,
  borderBottom: '1px solid #e2e8f0',
});

export const StyledHeaderLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  minHeight: '1.75rem',
  minWidth: 0,
});

export const StyledHeaderTitle = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  minWidth: 0,
});

export const StyledHeaderCopy = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minWidth: 0,
});

export const StyledHeaderText = styled.h2({
  margin: 0,
  fontFamily: 'FiraGO',
  fontSize: '0.95rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

export const StyledHeaderBadge = styled.span({
  color: '#62748e',
  fontFamily: "'FiraGO', sans-serif",
  fontSize: '0.625rem',
  fontWeight: 400,
  lineHeight: '1rem',
});

export const StyledCloseButton = styled.button({
  border: 'none',
  background: 'transparent',
  color: '#314158',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.75rem',
  height: '1.75rem',
  opacity: 0.85,
  padding: '0.375rem',
  ':hover': {
    opacity: 1,
  },
});

export const StyledRestartButton = styled.button({
  border: 'none',
  background: 'transparent',
  color: '#314158',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  opacity: 0.85,
  justifyContent: 'center',
  width: '1.75rem',
  height: '1.75rem',
  padding: '0.375rem',
  ':hover': {
    opacity: 1,
  },
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.4,
  },
});

export const StyledHeaderActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
});

export const StyledBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  padding: 0,
  gap: 0,
});

export const StyledConversation = styled.div({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '1rem',
  backgroundColor: '#f8fafc',
});

export const StyledThread = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.85rem',
});

export const StyledPromptBanner = styled.div({
  border: '1px solid #e2e8f0',
  background: '#f1f5f9',
  color: '#1d293d',
  textAlign: 'left',
  padding: '0.625rem 0.875rem',
  fontFamily:
    'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", ' +
    ' "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  cursor: 'default',
  width: 'auto',
  marginLeft: '2rem',
  marginRight: 0,
  alignSelf: 'flex-end',
  maxWidth: '92%',
  borderRadius: '0.625rem',
});

export const StyledResponseCard = styled.div<StyledResponseVariantProps>(({ variant }) => {
  const variants = {
    [RESPONSE_VARIANTS.READY]: {
      borderLeft: '4px solid #00bc7d',
    },
    [RESPONSE_VARIANTS.WARNING]: {
      borderLeft: '4px solid #fe9a00',
    },
    [RESPONSE_VARIANTS.ROUTING]: {
      borderLeft: '4px solid #0c2340',
    },
    [RESPONSE_VARIANTS.PLAIN]: {
      borderLeft: '1px solid rgba(15, 22, 33, 0.08)',
    },
  };

  return {
    border: '1px solid #e2e8f0',
    backgroundColor: white,
    padding: '0.875rem 1rem',
    alignSelf: 'flex-start',
    boxSizing: 'border-box',
    width: 'auto',
    overflow: 'hidden',
    marginLeft: 0,
    marginRight: '2rem',
    maxWidth: '95%',
    borderRadius: '0.625rem',
    ...variants[variant],
  };
});

export const StyledResponseLabel = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
  color: '#62748e',
  fontFamily: 'FiraGO',
  fontSize: '0.625rem',
  fontWeight: 'normal',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: '0.7rem',
});

export const StyledResponseIcon = styled.span<StyledResponseVariantProps>(({ variant }) => ({
  color:
    variant === RESPONSE_VARIANTS.WARNING
      ? '#fe9a00'
      : variant === RESPONSE_VARIANTS.READY
      ? '#00bc7d'
      : '#0c2340',
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 1,
}));

export const StyledResponseText = styled.p({
  margin: 0,
  color: '#1d293d',
  fontFamily:
    'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";',
  fontSize: '0.9rem',
  lineHeight: 1.625,
  whiteSpace: 'pre-wrap',
});

export const StyledUploadProgressTrack = styled.div({
  marginTop: '0.6rem',
  width: '100%',
  height: '0.35rem',
  borderRadius: '0.35rem',
  backgroundColor: '#d6e3ef',
  overflow: 'hidden',
});

export const StyledUploadProgressFill = styled('div', {
  shouldForwardProp: (prop) => prop !== 'progress',
})<{ progress: number }>(({ progress }) => ({
  width: `${progress}%`,
  height: '100%',
  backgroundColor: '#5cbdb9',
  transition: 'width 120ms ease-out',
}));

export const StyledResponseActions = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.55rem',
  marginTop: '0.85rem',
});

export const StyledCardButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'secondary' && prop !== 'selected',
})<StyledCardButtonProps>(({ secondary }) => ({
  border: `1px solid ${secondary ? '#cad5e2' : 'transparent'}`,
  backgroundColor: secondary ? 'transparent' : '#0c2340',
  backgroundImage: secondary
    ? 'unset'
    : 'linear-gradient(to right, #80B2FC 0%, #2BDACD 50%, #2BDACD 100%)',
  backgroundSize: secondary ? 'unset' : '200% auto',
  borderRadius: '1rem',
  color: secondary ? '#314158' : '#0f1621',
  padding: '0.25rem 0.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.45rem',
  fontFamily: 'FiraGO',
  fontSize: '0.6875rem',
  cursor: 'pointer',
  lineHeight: '1.25rem',
  ':hover': {
    backgroundColor: secondary ? white : '#1a3354',
  },
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.72,
  },
}));

export const StyledSelectedActionIcon = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  lineHeight: 1,
});

export const StyledInputRow = styled.form({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  gap: '0.5rem',
  flexShrink: 0,
  margin: '0.75rem',
  padding: '0.5rem 0.75rem',
  border: '1px solid #cbd5e1',
  borderRadius: '0.75rem',
  backgroundColor: white,
  ':focus-within': {
    borderColor: aqua,
    boxShadow: '0 0 0 2px rgba(34, 205, 200, 0.16)',
  },
});

export const StyledUploadButton = styled.button({
  border: 'none',
  width: '2rem',
  height: '2rem',
  padding: 0,
  borderRadius: '999px',
  backgroundColor: 'transparent',
  color: '#64748b',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ':hover': {
    color: black,
    backgroundColor: '#f1f5f9',
  },
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
});

export const StyledInput = styled.input({
  minWidth: 0,
  border: 'none',
  padding: '0 0.25rem',
  fontFamily: 'FiraGO',
  fontSize: '0.875rem',
  lineHeight: '2rem',
  color: black,
  borderRadius: '0.5rem',
  '::placeholder': {
    color: '#868a95',
  },
  ':focus': {
    outline: 'none',
  },
});

const dotBounce = keyframes({
  '0%, 80%, 100%': { transform: 'scale(0)', opacity: 0.4 },
  '40%': { transform: 'scale(1)', opacity: 1 },
});

export const StyledSendButton = styled.button({
  border: 'none',
  width: '2rem',
  padding: '3px 3px 0 0',
  lineHeight: 'calc(2rem - 3px)',
  cursor: 'pointer',
  fontSize: '1.15rem',
  color: '#0f1621',
  backgroundImage: `linear-gradient(90deg, ${aqua}, ${secondaryColor})`,
  backgroundSize: '200% auto',
  borderRadius: '1rem',
  transition: 'all 0.3s ease',
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  ':hover:enabled': {
    backgroundPosition: 'right center',
  },
});

export const StyledTypingIndicator = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  height: '1rem',
  '& span': {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#5cbdb9',
    animation: `${dotBounce} 1.4s infinite ease-in-out both`,
    '&:nth-of-type(1)': { animationDelay: '-0.32s' },
    '&:nth-of-type(2)': { animationDelay: '-0.16s' },
  },
});

export const StyledHiddenInput = styled.input({
  display: 'none',
});
